import { cache, type InvalidateCacheEntry, REALTIME_INVALIDATION_SOURCE } from '@customerio/hydra-nav-state';
import { useEffect } from 'react';
import { envChannel } from './channels';
import { toRealtimeEvent, type ActiveRealtime, type RealtimeEvent } from './events';
import type { Actor } from './message';

/** The only entity type this layer re-announces so far; the Design Studio editor refreshes the node. */
export const DS_NODE_ENTITY_TYPE = 'ds_node';

/** Changes arriving within this long of the first one are handled together. */
export const COALESCE_MS = 250;

/** Who made a pending change; `undefined` when the server did not attribute it. */
type PendingActor = Actor | undefined;

/**
 * Collects `ds_node` changes and signals them through `cache.invalidate` in bursts. An empty
 * node id means "every node" (the server sends it for bulk writes); the entry then leaves `id`
 * off, which is how `InvalidateCacheEntry` spells a type-wide refresh.
 */
export function createDsNodeInvalidator() {
  const pending = new Map<string, PendingActor>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush() {
    timer = null;
    const entries = [...pending].map(([id, actor]): InvalidateCacheEntry => {
      const entry: InvalidateCacheEntry = { type: DS_NODE_ENTITY_TYPE, source: REALTIME_INVALIDATION_SOURCE };
      if (id !== '') entry.id = id;
      if (actor !== undefined) entry.actor = { userId: actor.userId, source: actor.source };
      return entry;
    });
    pending.clear();
    if (entries.length > 0) cache.invalidate(entries);
  }

  function queue(id: string, actor: PendingActor) {
    pending.set(id, actor);
    timer ??= setTimeout(flush, COALESCE_MS);
  }

  return {
    handle(event: RealtimeEvent) {
      if (event.kind === 'message' && event.message.type === 'entity.changed') {
        const { data, actor } = event.message;
        if (data.entityType === DS_NODE_ENTITY_TYPE) queue(data.entityId, actor);
      } else if (event.kind === 'subscribed' && event.wasRecovering && !event.recovered) {
        // The server could not replay what this tab missed while offline: refresh every node.
        queue('', undefined);
      }
    },
    dispose() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending.clear();
    },
  };
}

/**
 * Turns server `entity.changed` messages about Design Studio nodes into `cache.invalidate`
 * signals, which the editor (parcel) listens to. Other entity types are ignored. Changes made by
 * this tab come back too; the editor refetching a node it just saved is harmless.
 */
export function useDsNodeChanges(realtime: ActiveRealtime | null) {
  const transport = realtime?.transport;
  const environmentId = realtime?.environmentId;

  useEffect(() => {
    if (transport === undefined || environmentId === undefined) return;
    const invalidator = createDsNodeInvalidator();
    const unsubscribe = transport.subscribe(envChannel(environmentId), (raw) => {
      const event = toRealtimeEvent(raw);
      if (event !== null) invalidator.handle(event);
    });
    return () => {
      unsubscribe();
      invalidator.dispose();
    };
  }, [transport, environmentId]);
}
